import { useEffect, useState } from "react";

import { createPortal } from "react-dom";

import { Button } from "../components/Button";
import {
  adminGetEmail,
  adminListEmails,
  adminListSms,
  adminRetryEmail,
  adminListUsers,
  adminLoginAs,
  adminRunDrip,
  adminSendDrip,
  type AdminEmail,
  type AdminEmailDetail,
  type AdminSms,
  type AdminUser,
  type DripRunResult,
  type DripSendResult,
  type Me,
} from "../api";

const DRIP_LESSONS = ["invite", "recurring", "beacons", "ladders"] as const;

/**
 * Secret admin panel. URL: /admin.
 *
 * Only visible to emails in the agent's ADMIN_EMAILS env var. Anyone else
 * gets a 403 (or 404 if the admin panel is disabled entirely).
 */
export function Admin({ me }: { me: Me }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<"forbidden" | "notfound" | string | null>(null);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [view, setView] = useState<"users" | "emails" | "sms" | "drip">("users");

  useEffect(() => {
    let cancelled = false;
    adminListUsers()
      .then((res) => {
        if (cancelled) return;
        if (res === "forbidden") return setError("forbidden");
        if (res === "notfound") return setError("notfound");
        setUsers(res.users);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "failed");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function loginAs(u: AdminUser) {
    if (u.id === me.uid) {
      // Already signed in as that user — just go to chat.
      window.location.assign("/");
      return;
    }
    if (
      !window.confirm(
        `Switch to ${u.email}? A banner will let you return to your admin account in one click.`,
      )
    )
      return;
    setBusyUserId(u.id);
    try {
      await adminLoginAs(u.id);
      window.location.assign("/");
    } catch (err) {
      setBusyUserId(null);
      alert(err instanceof Error ? err.message : "failed");
    }
  }

  if (error === "forbidden" || error === "notfound") {
    return <ForbiddenScreen reason={error} />;
  }

  const filtered =
    users && filter
      ? users.filter((u) => {
          const q = filter.toLowerCase();
          return (
            u.email.toLowerCase().includes(q) ||
            (u.name?.toLowerCase().includes(q) ?? false)
          );
        })
      : users;

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <header className="mb-8 flex items-center justify-between gap-4">
        <div>
          <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-accent">
            // Internal · Admin · {me.email}
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-paper">
            ClubBot Admin
          </h1>
        </div>
        <a
          href="/"
          className="font-mono text-xs text-paper-muted hover:text-paper"
        >
          ← back to chat
        </a>
      </header>

      <div className="mb-6 flex gap-2 font-mono text-[11px] uppercase tracking-wider">
        {(["users", "emails", "sms", "drip"] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={
              "rounded px-3 py-1.5 " +
              (view === v ? "bg-accent text-bg" : "text-paper-muted hover:text-paper")
            }
          >
            {v}
          </button>
        ))}
      </div>

      {view === "emails" && <EmailsView />}

      {view === "sms" && <SmsView />}

      {view === "drip" && <DripView me={me} />}

      {view === "users" && (
      <>
      <div className="mb-4 flex items-center gap-3">
        <input
          type="text"
          placeholder="filter by email or name…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1"
        />
        <span className="font-mono text-xs text-paper-dim">
          {users == null
            ? "loading…"
            : `${filtered?.length ?? 0} / ${users.length} users`}
        </span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-line bg-bg-2">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-line bg-bg-3 text-paper-muted">
            <tr className="font-mono text-[11px] uppercase tracking-wider">
              <th className="px-4 py-3">email</th>
              <th className="px-4 py-3">name</th>
              <th className="px-4 py-3">sport</th>
              <th className="px-4 py-3 text-right">skill</th>
              <th className="px-4 py-3 text-right">groups</th>
              <th className="px-4 py-3">timezone</th>
              <th className="px-4 py-3">created</th>
              <th className="px-4 py-3 text-right">action</th>
            </tr>
          </thead>
          <tbody>
            {users == null ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-paper-dim">
                  <span className="font-mono text-xs">loading…</span>
                </td>
              </tr>
            ) : filtered && filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-paper-dim">
                  <span className="font-mono text-xs">no matches</span>
                </td>
              </tr>
            ) : (
              filtered?.map((u) => (
                <tr
                  key={u.id}
                  className={`border-t border-line transition-colors hover:bg-bg-3 ${
                    u.id === me.uid ? "bg-bg-3/60" : ""
                  }`}
                >
                  <td className="px-4 py-2.5 font-mono text-xs text-paper">
                    {u.email}
                    {u.id === me.uid && (
                      <span className="ml-2 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-accent">
                        you
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-paper-muted">{u.name ?? "—"}</td>
                  <td className="px-4 py-2.5 text-paper-muted">{u.sport ?? "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-paper-muted">
                    {u.skill ?? "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-paper-muted">
                    {u.group_count}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-paper-dim">
                    {u.timezone ?? "—"}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-paper-dim">
                    {fmtDate(u.created_at)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      variant="secondary"
                      onClick={() => loginAs(u)}
                      disabled={busyUserId === u.id}
                      className="!px-3 !py-1 !text-xs"
                    >
                      {busyUserId === u.id
                        ? "switching…"
                        : u.id === me.uid
                          ? "open"
                          : "login as"}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-6 font-mono text-[11px] text-paper-dim">
        // Login-as writes an audit log line. While impersonating, a red banner at
        the top lets you return to your admin account in one click.
      </p>
      </>
      )}
    </div>
  );
}

function DripView({ me }: { me: Me }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DripRunResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function run() {
    if (
      !window.confirm(
        "Send the admin-education drip now? Eligible admins get their next unsent lesson — this respects the ~2-day spacing, the new-club grace window, and opt-outs, so it's safe to re-run.",
      )
    )
      return;
    setRunning(true);
    setErr(null);
    adminRunDrip()
      .then(setResult)
      .catch((e) => setErr(e instanceof Error ? e.message : "failed"))
      .finally(() => setRunning(false));
  }

  return (
    <div className="max-w-2xl">
      <DripTestForm defaultEmail={me.email} />

      <hr className="my-8 border-line" />

      <h2 className="mb-2 font-display text-lg font-semibold text-paper">
        Run the full drip
      </h2>
      <p className="mb-5 text-[15px] leading-relaxed text-paper-muted">
        Dispatches the admin-education drip across every club — the same logic
        the daily cron runs. Each eligible admin gets at most their next unsent,
        relevant lesson (invite → recurring → beacons → ladders), skipping
        anything they've already adopted.
      </p>
      <Button variant="primary" onClick={run} disabled={running}>
        {running ? "running…" : "Run drip now"}
      </Button>
      {err && <p className="mt-3 font-mono text-xs text-red-400">{err}</p>}
      {result && (
        <div className="mt-6 rounded-xl border border-line bg-bg-2 p-4 font-mono text-xs">
          <div className="mb-2 text-paper">// last run</div>
          {Object.entries(result)
            .filter(([k]) => k !== "ok")
            .map(([k, v]) => (
              <div key={k} className="flex justify-between gap-8 py-0.5">
                <span className="text-paper-dim">{k}</span>
                <span className="text-paper">{String(v)}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function DripTestForm({ defaultEmail }: { defaultEmail: string }) {
  const [email, setEmail] = useState(defaultEmail);
  const [lesson, setLesson] = useState<string>(""); // "" = next adaptive
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<DripSendResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function send() {
    setSending(true);
    setErr(null);
    setSent(null);
    adminSendDrip({ email: email.trim(), lesson: lesson || undefined })
      .then(setSent)
      .catch((e) => setErr(e instanceof Error ? e.message : "failed"))
      .finally(() => setSending(false));
  }

  return (
    <div>
      <h2 className="mb-2 font-display text-lg font-semibold text-paper">
        Send a test lesson to one admin
      </h2>
      <p className="mb-4 text-[15px] leading-relaxed text-paper-muted">
        Force-sends a single lesson to one admin so you can see the email —
        bypasses spacing, grace, adoption, and opt-outs, and is safe to re-fire.
        Uses the admin's oldest club for context. In dev it shows up in the{" "}
        <span className="font-mono text-xs">emails</span> tab as a{" "}
        <span className="font-mono text-xs">dev</span> row you can preview.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[11px] uppercase tracking-wider text-paper-dim">
            admin email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
            className="min-w-[240px]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[11px] uppercase tracking-wider text-paper-dim">
            lesson
          </span>
          <select
            value={lesson}
            onChange={(e) => setLesson(e.target.value)}
            className="font-mono text-xs"
          >
            <option value="">next (adaptive)</option>
            {DRIP_LESSONS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <Button
          variant="primary"
          onClick={send}
          disabled={sending || !email.trim()}
        >
          {sending ? "sending…" : "Send test"}
        </Button>
      </div>
      {err && <p className="mt-3 font-mono text-xs text-red-400">{err}</p>}
      {sent && (
        <p className="mt-3 font-mono text-xs text-accent">
          ✓ sent “{sent.lesson}” to {sent.to_email} (club: {sent.group_name})
        </p>
      )}
    </div>
  );
}

function EmailsView() {
  const [emails, setEmails] = useState<AdminEmail[] | null>(null);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdminEmailDetail | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  function openEmail(id: string) {
    adminGetEmail(id)
      .then(setSelected)
      .catch((e) => setErr(e instanceof Error ? e.message : "failed"));
  }

  useEffect(() => {
    let cancelled = false;
    setEmails(null);
    adminListEmails({ kind: kind || undefined, status: status || undefined })
      .then((res) => {
        if (cancelled) return;
        if (res === "forbidden" || res === "notfound") return setErr(res);
        setEmails(res.emails);
      })
      .catch((e) => !cancelled && setErr(e instanceof Error ? e.message : "failed"));
    return () => {
      cancelled = true;
    };
  }, [kind, status, refreshKey]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="kind (e.g. weekly_digest)…"
          value={kind}
          onChange={(e) => setKind(e.target.value.trim())}
          className="flex-1 min-w-[180px]"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="font-mono text-xs"
        >
          <option value="">all statuses</option>
          <option value="sent">sent</option>
          <option value="failed">failed</option>
          <option value="dev">dev</option>
        </select>
        <span className="font-mono text-xs text-paper-dim">
          {emails == null ? "loading…" : `${emails.length} emails`}
        </span>
      </div>

      {err && <p className="mb-3 font-mono text-xs text-red-400">{err}</p>}

      <div className="overflow-x-auto rounded-xl border border-line bg-bg-2">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-line bg-bg-3 text-paper-muted">
            <tr className="font-mono text-[11px] uppercase tracking-wider">
              <th className="px-4 py-3">when</th>
              <th className="px-4 py-3">dir</th>
              <th className="px-4 py-3">status</th>
              <th className="px-4 py-3">kind</th>
              <th className="px-4 py-3">to / from</th>
              <th className="px-4 py-3">subject</th>
              <th className="px-4 py-3">provider id / error</th>
            </tr>
          </thead>
          <tbody>
            {emails == null ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-paper-dim">
                  <span className="font-mono text-xs">loading…</span>
                </td>
              </tr>
            ) : emails.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-paper-dim">
                  <span className="font-mono text-xs">no emails</span>
                </td>
              </tr>
            ) : (
              emails.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => openEmail(e.id)}
                  className="border-t border-line hover:bg-bg-3 cursor-pointer"
                >
                  <td className="px-4 py-2.5 font-mono text-[11px] text-paper-dim whitespace-nowrap">
                    {fmtTime(e.created_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={
                        "rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider " +
                        (e.direction === "in"
                          ? "bg-success/15 text-success"
                          : "bg-paper/10 text-paper-dim")
                      }
                    >
                      {e.direction === "in" ? "in" : "out"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={
                        "rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider " +
                        (e.status === "sent"
                          ? "bg-accent/15 text-accent"
                          : e.status === "failed"
                            ? "bg-red-500/15 text-red-400"
                            : "bg-paper/10 text-paper-dim")
                      }
                    >
                      {e.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-paper-muted">{e.kind}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-paper">{e.to_email}</td>
                  <td className="px-4 py-2.5 text-paper-muted">{e.subject}</td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-paper-dim max-w-[260px] truncate">
                    {e.error ?? e.provider_message_id ?? "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <EmailPreviewModal
          email={selected}
          onClose={() => setSelected(null)}
          onRetried={() => {
            setSelected(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

function SmsView() {
  const [sms, setSms] = useState<AdminSms[] | null>(null);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [direction, setDirection] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdminSms | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSms(null);
    adminListSms({
      kind: kind || undefined,
      status: status || undefined,
      direction: direction || undefined,
    })
      .then((res) => {
        if (cancelled) return;
        if (res === "forbidden" || res === "notfound") return setErr(res);
        setSms(res.sms);
      })
      .catch((e) => !cancelled && setErr(e instanceof Error ? e.message : "failed"));
    return () => {
      cancelled = true;
    };
  }, [kind, status, direction]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="kind (e.g. beacon.broadcast)…"
          value={kind}
          onChange={(e) => setKind(e.target.value.trim())}
          className="flex-1 min-w-[180px]"
        />
        <select
          value={direction}
          onChange={(e) => setDirection(e.target.value)}
          className="font-mono text-xs"
        >
          <option value="">in + out</option>
          <option value="out">out</option>
          <option value="in">in</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="font-mono text-xs"
        >
          <option value="">all statuses</option>
          <option value="sent">sent</option>
          <option value="failed">failed</option>
          <option value="dev">dev</option>
          <option value="received">received</option>
        </select>
        <span className="font-mono text-xs text-paper-dim">
          {sms == null ? "loading…" : `${sms.length} texts`}
        </span>
      </div>

      {err && <p className="mb-3 font-mono text-xs text-red-400">{err}</p>}

      <div className="overflow-x-auto rounded-xl border border-line bg-bg-2">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-line bg-bg-3 text-paper-muted">
            <tr className="font-mono text-[11px] uppercase tracking-wider">
              <th className="px-4 py-3">when</th>
              <th className="px-4 py-3">dir</th>
              <th className="px-4 py-3">status</th>
              <th className="px-4 py-3">kind</th>
              <th className="px-4 py-3">to / from</th>
              <th className="px-4 py-3">body</th>
              <th className="px-4 py-3">provider id / error</th>
            </tr>
          </thead>
          <tbody>
            {sms == null ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-paper-dim">
                  <span className="font-mono text-xs">loading…</span>
                </td>
              </tr>
            ) : sms.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-paper-dim">
                  <span className="font-mono text-xs">no texts</span>
                </td>
              </tr>
            ) : (
              sms.map((m) => (
                <tr
                  key={m.id}
                  onClick={() => setSelected(m)}
                  className="border-t border-line hover:bg-bg-3 cursor-pointer"
                >
                  <td className="px-4 py-2.5 font-mono text-[11px] text-paper-dim whitespace-nowrap">
                    {fmtTime(m.created_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={
                        "rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider " +
                        (m.direction === "in"
                          ? "bg-success/15 text-success"
                          : "bg-paper/10 text-paper-dim")
                      }
                    >
                      {m.direction === "in" ? "in" : "out"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={
                        "rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider " +
                        (m.status === "sent"
                          ? "bg-accent/15 text-accent"
                          : m.status === "failed"
                            ? "bg-red-500/15 text-red-400"
                            : m.status === "received"
                              ? "bg-success/15 text-success"
                              : "bg-paper/10 text-paper-dim")
                      }
                    >
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-paper-muted">{m.kind}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-paper whitespace-nowrap">
                    {m.direction === "in" ? (m.from_number ?? "—") : m.to_number}
                  </td>
                  <td className="px-4 py-2.5 text-paper-muted max-w-[320px] truncate">
                    {m.body ?? "—"}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-paper-dim max-w-[220px] truncate">
                    {m.error ?? m.provider_message_id ?? "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <SmsDetailModal sms={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function SmsDetailModal({ sms, onClose }: { sms: AdminSms; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-line bg-bg-2"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="border-b border-line p-4">
          <div className="mb-1 flex items-start justify-between gap-3">
            <h2 className="font-display text-lg font-semibold text-paper">
              {sms.direction === "in" ? "Inbound text" : "Outbound text"} · {sms.kind}
            </h2>
            <button
              onClick={onClose}
              className="shrink-0 font-mono text-xs text-paper-muted hover:text-paper"
            >
              ✕ close
            </button>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-paper-dim">
            <span>to {sms.to_number}</span>
            {sms.from_number && <span>· from {sms.from_number}</span>}
            <span>· {sms.status}</span>
            {sms.segments != null && <span>· {sms.segments} seg</span>}
            <span>· {fmtTime(sms.created_at)}</span>
            {sms.provider_message_id && <span>· id {sms.provider_message_id}</span>}
          </div>
          {sms.error && (
            <p className="mt-2 font-mono text-[11px] text-red-400">{sms.error}</p>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-bg-3 p-4">
          <pre className="whitespace-pre-wrap text-sm leading-relaxed text-paper">
            {sms.body ?? "(no body)"}
          </pre>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function EmailPreviewModal({
  email,
  onClose,
  onRetried,
}: {
  email: AdminEmailDetail;
  onClose: () => void;
  onRetried: () => void;
}) {
  const [tab, setTab] = useState<"html" | "text">(
    email.body_html ? "html" : "text",
  );
  const [retrying, setRetrying] = useState(false);
  const [retryErr, setRetryErr] = useState<string | null>(null);
  const canRetry = email.status === "failed" && !!email.body_html && !!email.body_text;

  function retry() {
    setRetrying(true);
    setRetryErr(null);
    adminRetryEmail(email.id)
      .then((res) => {
        if (res.status === "sent" || res.status === "dev") {
          onRetried();
        } else {
          setRetryErr("send failed again — check the new log row");
          setRetrying(false);
        }
      })
      .catch((e) => {
        setRetryErr(e instanceof Error ? e.message : "retry failed");
        setRetrying(false);
      });
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-line bg-bg-2"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="border-b border-line p-4">
          <div className="mb-1 flex items-start justify-between gap-3">
            <h2 className="font-display text-lg font-semibold text-paper">
              {email.subject}
            </h2>
            <div className="flex shrink-0 items-center gap-3">
              {canRetry && (
                <button
                  onClick={retry}
                  disabled={retrying}
                  className="rounded bg-accent px-2.5 py-1 font-mono text-xs text-bg hover:opacity-90 disabled:opacity-50"
                >
                  {retrying ? "retrying…" : "↻ retry"}
                </button>
              )}
              <button
                onClick={onClose}
                className="font-mono text-xs text-paper-muted hover:text-paper"
              >
                ✕ close
              </button>
            </div>
          </div>
          {retryErr && (
            <p className="mb-1 font-mono text-[11px] text-red-400">{retryErr}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-paper-dim">
            <span>{email.direction === "in" ? "from" : "to"} {email.to_email}</span>
            <span>· {email.kind}</span>
            <span>· {email.status}</span>
            <span>· {fmtTime(email.created_at)}</span>
            {email.provider_message_id && <span>· id {email.provider_message_id}</span>}
          </div>
          {email.error && (
            <p className="mt-2 font-mono text-[11px] text-red-400">{email.error}</p>
          )}
        </div>

        <div className="flex gap-2 border-b border-line px-4 py-2 font-mono text-[11px] uppercase tracking-wider">
          {(["html", "text"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={
                "rounded px-2.5 py-1 " +
                (tab === t ? "bg-accent text-bg" : "text-paper-muted hover:text-paper")
              }
            >
              {t}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-bg-3">
          {tab === "html" ? (
            email.body_html ? (
              // Sandboxed: email HTML renders isolated, scripts disabled.
              <iframe
                title="email preview"
                sandbox=""
                srcDoc={email.body_html}
                className="h-[60vh] w-full border-0 bg-white"
              />
            ) : (
              <p className="p-6 text-center font-mono text-xs text-paper-dim">
                no HTML body stored for this email
              </p>
            )
          ) : (
            <pre className="whitespace-pre-wrap p-4 text-xs leading-relaxed text-paper-muted">
              {email.body_text ?? "no text body stored for this email"}
            </pre>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function ForbiddenScreen({ reason }: { reason: "forbidden" | "notfound" }) {
  return (
    <div className="grid min-h-full place-items-center px-6 py-16">
      <div className="w-full max-w-md text-center">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-red-400">
          // {reason === "forbidden" ? "Forbidden" : "Not found"}
        </p>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-paper">
          You're not on the admin list.
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-paper-muted">
          {reason === "forbidden"
            ? "Your account isn't authorized to access this page."
            : "There's nothing here."}
        </p>
        <a
          href="/"
          className="mt-6 inline-block font-mono text-xs text-paper-muted underline-offset-4 hover:text-paper hover:underline"
        >
          ← back to chat
        </a>
      </div>
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "2-digit",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}
